<template>
  <v-dialog v-model="showDialog" width="600" scrollable persistent>
    <v-card>
      <v-card-title>
        {{ t("categories.ai.title") }}
      </v-card-title>
      <v-card-text>
        <div class="text-sm text-medium-emphasis mb-4">
          {{ t("categories.ai.description") }}
        </div>

        <v-file-upload
          v-model="file"
          show-size
          :accept="acceptedFileTypes"
          :disabled="loading"
          :title="t('categories.ai.fileUploadTitle')"
          :rules="[maxFileSize(maxFileSizeMB)]"
          class="mb-4"
        ></v-file-upload>

        <v-textarea
          v-model="instructions"
          :label="t('categories.ai.instructionsLabel')"
          variant="outlined"
          rows="3"
          auto-grow
          :disabled="loading"
          class="mb-2"
        />

        <v-alert
          v-if="error"
          color="error"
          type="error"
          variant="tonal"
          class="mt-4"
          closable
          @click:close="error = ''"
        >
          {{ error }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="closeDialog()">{{ t("common.cancel") }}</v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="loading"
          :disabled="!file || loading"
          @click="generateCategories()"
        >
          {{ t("categories.ai.generate") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { call } from "@/firebase";
import type { TrackedCategoryDirection } from "@aircap/common/trackedCategory";
import { captureException } from "@sentry/vue";
import { computed, ref, watch } from "vue";
import { VFileUpload } from "vuetify/components/VFileUpload";

const { t } = useI18n();
const { maxFileSize } = useRules();

const props = defineProps<{
  accountId: string;
  modelValue: boolean;
  direction: TrackedCategoryDirection;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  "import:categories": [categories: Array<{ name: string; amount: number; groupName?: string }>];
}>();

const showDialog = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const file = ref<File | undefined>(undefined);
const instructions = ref("");
const loading = ref(false);
const error = ref("");
let importAbort: AbortController | undefined;

/** Same limit as other document uploads (Swan banking, contracts, storage rules). */
const maxFileSizeMB = 32;

const acceptedFileTypes = ".txt,.csv,.json,.pdf,.xls,.xlsx";

const ACCEPTED_EXTENSIONS = [".txt", ".csv", ".json", ".pdf", ".xls", ".xlsx"];

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

watch(showDialog, (value) => {
  if (!value) {
    importAbort?.abort();
    loading.value = false;
    file.value = undefined;
    instructions.value = "";
    error.value = "";
  }
});

const closeDialog = () => {
  showDialog.value = false;
};

const generateCategories = async () => {
  if (!file.value) return;

  if (!isAcceptedFile(file.value)) {
    error.value = t("categories.ai.errorFileType");
    return;
  }

  if (file.value.size > maxFileSizeMB * 1024 * 1024) {
    error.value = t("errors.fileTooBig", { maxFileSizeMB });
    return;
  }

  importAbort?.abort();
  const abort = new AbortController();
  importAbort = abort;

  loading.value = true;
  error.value = "";

  try {
    const f = file.value;
    const fileData = [
      {
        name: f.name,
        mimeType: f.type || "application/octet-stream",
        content: await fileToBase64(f, abort.signal),
      },
    ];

    if (abort.signal.aborted) return;

    const result = await call("generateTrackedCategories")({
      accountId: props.accountId,
      prompt: instructions.value.trim(),
      direction: props.direction,
      files: fileData,
    });

    if (abort.signal.aborted) return;

    const categories = result.map((category) => ({
      name: category.name,
      amount: category.amount ?? 0,
      ...(category.groupName ? { groupName: category.groupName } : {}),
    }));

    emit("import:categories", categories);
    closeDialog();
  } catch (cause) {
    if (abort.signal.aborted || (cause instanceof DOMException && cause.name === "AbortError"))
      return;
    const wrappedError = new Error("Error generating categories", { cause });
    console.error(wrappedError);
    captureException(wrappedError);
    error.value = t("categories.ai.error");
  } finally {
    if (importAbort === abort) loading.value = false;
  }
};

const fileToBase64 = (file: File, signal: AbortSignal) => {
  return new Promise<string>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }

    const reader = new FileReader();
    const onAbort = () => {
      reader.abort();
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
    reader.onload = () => {
      signal.removeEventListener("abort", onAbort);
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read file"));
        return;
      }
      resolve(result.split(",")[1] || "");
    };
    reader.onerror = () => {
      signal.removeEventListener("abort", onAbort);
      reject(reader.error ?? new Error("Failed to read file"));
    };
    reader.readAsDataURL(file);
  });
};
</script>

<style scoped>
:deep(.v-file-upload-title) {
  font-size: 1.1rem;
  font-weight: 600;
}
</style>
